import FuseNavigation from '@fuse/core/FuseNavigation';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthService } from 'app/services';
import * as authActions from 'app/auth/store/actions';

import ROLE_LEVEL from 'app/constants/RoleLevel';
import configs from 'app/Config';
import { makeStyles } from '@material-ui/core';

import { mypakAdminGddConfig, getNavigationConfig } from 'app/fuse-configs/navigationConfig';
import { MENU_ENUM } from 'app/main/role/constants';
import LogoFull from './assets/myPak-NG-Logo-Full.png';
import LogoShort from './assets/myPak-NG-Logo-NG.png';

//
const modifyNavigationByReportPermissions = (navigationConfig, userPermissions) => {
  const reportEntryPermissions = userPermissions[MENU_ENUM.REPORT]?.entryPermissions || [];
  if (reportEntryPermissions.length === 0) return navigationConfig;

  return navigationConfig
    .map((item) => {
      if (item.children) {
        const filteredChildren = item.children.filter((child) => {
          if (item.id === 'report') {
            switch (child.id) {
              case 'patientReport':
                return reportEntryPermissions.some((p) => p.code === 'reportPatientReports' && p.isAllowed);
              case 'analyticReport':
                return reportEntryPermissions.some((p) => p.code === 'reportSummaryReports' && p.isAllowed);
              case 'scriptRequest':
                return reportEntryPermissions.some((p) => p.code === 'reportScriptRequestsReports' && p.isAllowed);
              case 'ppaClaimReport':
                return reportEntryPermissions.some((p) => p.code === 'reportPpaClaimReports' && p.isAllowed);
              default:
                return true;
            }
          }
          return true;
        });

        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...item,
          children: filteredChildren,
        };
      }
      return item;
    })
    .filter(Boolean);
};

//
const modifyNavigationByPharmacyConfig = (navigationConfig, pharmacyConfig) => {
  const pharmacyFeatures = {
    qscript: pharmacyConfig?.enableQScript === 'true',
    vpb: pharmacyConfig?.enableVPB === 'true',
    dispenseTracker: pharmacyConfig?.enableDispenseTracker === 'true',
  };

  return navigationConfig.filter((item) => {
    const featureKey = item.id;
    return pharmacyFeatures[featureKey] !== false;
  });
};

const useStyles = makeStyles(() => ({
  version: {
    color: 'white',
    marginLeft: '24px',
    marginBottom: '8px',
    opacity: 0,
  },
  logo: {
    height: 45,
    marginTop: 5,
    marginBottom: 20,
    '& img': {
      height: 30,
      width: 50,
    },
  },
  logoFull: {
    height: 45,
    marginTop: 5,
    marginBottom: 20,
    '& img': {
      height: 45,
      width: 150,
    },
  },
}));

function Navigation(props) {
  const {
    navigation: { navigationConfig, readOnlyNavigationConfig, superAdminNavigationConfig, navigationBottomConfig },
    navbar: { foldedOpen },
  } = useSelector(({ fuse }) => fuse);
  const {
    user: { data },
  } = useSelector((state) => state.auth);

  const classes = useStyles({ foldedOpen });
  const [isActive, setIsActive] = useState(false);

  const pharmacyConfig = useSelector((state) => state.pharmacy?.update?.configuration?.data);
  const userPermissions = useSelector((state) => state.auth.user.entryPermission);

  // Get pharmacy config to check read-only mode
  const isReadOnlyMode = pharmacyConfig?.readOnlyMode || false;

  // Method to select navigation configuration
  const selectNavigationConfig = () => {
    if (data?.roleLevel === ROLE_LEVEL.SUPER_ADMIN) {
      return superAdminNavigationConfig;
    }
    if (data?.roleLevel === ROLE_LEVEL.DRUG_ADMIN) {
      return mypakAdminGddConfig;
    }
    if (isReadOnlyMode) {
      return readOnlyNavigationConfig;
    }

    let userNavigationConfig = navigationConfig;
    // Modify navigation configurations base on user's permissions
    userNavigationConfig = modifyNavigationByReportPermissions(userNavigationConfig, userPermissions);
    // Modify navigation configurations base on pharmacy's setting
    userNavigationConfig = modifyNavigationByPharmacyConfig(userNavigationConfig, pharmacyConfig);

    return userNavigationConfig;
  };

  return (
    <div
      className="flex flex-col justify-between h-full"
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
    >
      <div className="top">
        <div className={clsx('px-6 flex justify-center items-center', foldedOpen ? classes.logoFull : classes.logo)}>
          <img src={foldedOpen ? LogoFull : LogoShort} alt="myPak" />
        </div>

        <FuseNavigation
          className={clsx('navigation', props.className)}
          navigation={selectNavigationConfig()}
          layout={props.layout}
          dense={props.dense}
          active={props.active}
        />
      </div>
      <div className="bottom">
        <FuseNavigation
          className={clsx('navigation', props.className)}
          navigation={navigationBottomConfig}
          layout={props.layout}
          dense={props.dense}
          active={props.active}
          isActive={isActive}
        />
        <div
          className={clsx(
            classes.version,
            foldedOpen ? 'opacity-100' : 'opacity-0',
            'text-15',
            'whitespace-no-wrap',
            'cursor-default',
          )}
        >
          V.{configs.REACT_APP_VERSION}
        </div>
      </div>
    </div>
  );
}

Navigation.defaultProps = {
  layout: 'vertical',
};

export default Navigation;
